import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TripChatRoom from "@/components/chat/TripChatRoom";
import { normalizeSenderRole } from "@/components/chat/tripChatRoomUtils";
import { DriverAPI } from "./config";
import DriverBrandHeader from "./DriverBrandHeader";
import { getDriverLanguage } from "./driverI18n";
import { getDriverFeatureText, getLocalizedDriverApiError } from "./driverFeatureI18n";
import { getDriverChatApiLanguage, getDriverChatText } from "./driverChatI18n";

export default function DriverTripChat() {
  const currentRole = "driver";
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnsToConversationList = location.state?.driverChatListReturn === true;
  const handleBack = () => navigate(
    returnsToConversationList ? "/driver/orders" : `/driver/orders/${orderId}`,
    returnsToConversationList ? { state: { driverChatListReturn: true } } : undefined
  );
  const [chatData, setChatData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const driverLanguage = getDriverLanguage();
  const [language, setLanguage] = useState(() => getDriverChatApiLanguage(driverLanguage));
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [hasNotificationNotice, setHasNotificationNotice] = useState(false);
  const latestIncomingMessageIdRef = useRef("");
  const clearMessageNotice = useCallback(() => setHasNotificationNotice(false), []);
  const copy = {
    ...getDriverChatText(language),
    ...getDriverFeatureText(language).chat,
  };

  const updateIncomingMessageNotice = useCallback((nextChatData) => {
    const messages = nextChatData?.messages || [];
    const latestIncoming = [...messages].reverse().find((message) => (
      !message.deleted_at && normalizeSenderRole(message, nextChatData?.order) !== currentRole
    ));
    if (!latestIncoming?.id) return;
    if (latestIncomingMessageIdRef.current && latestIncomingMessageIdRef.current !== latestIncoming.id) {
      setHasNotificationNotice(true);
      document.title = copy.documentTitle;
      if (navigator.vibrate) {
        navigator.vibrate(120);
      }
    }
    latestIncomingMessageIdRef.current = latestIncoming.id;
  }, [copy.documentTitle]);

  const fetchChat = useCallback(async () => {
    try {
      const [chat, templateData] = await Promise.all([
        DriverAPI.getTripChat(orderId, language),
        DriverAPI.getChatTemplates(language),
      ]);
      const defaultTemplates = templateData.default_templates || [];
      const savedTemplates = templateData.custom_templates || [];
      updateIncomingMessageNotice(chat);
      setChatData(chat);
      setCustomTemplates(savedTemplates);
      setTemplates([...defaultTemplates, ...savedTemplates]);
      setError("");
    } catch (err) {
      setError(getLocalizedDriverApiError(err, copy.unavailable, language));
    } finally {
      setLoading(false);
    }
  }, [copy.unavailable, language, orderId, updateIncomingMessageNotice]);

  useEffect(() => {
    fetchChat();
  }, [fetchChat]);

  const fetchTripChatMessages = useCallback(async (options = {}) => {
    const page = await DriverAPI.getTripChat(orderId, language, options);
    updateIncomingMessageNotice(page);
    return page;
  }, [language, orderId, updateIncomingMessageNotice]);

  const handleSendMessage = async (body, sourceLanguage, attachments = []) => {
    setSending(true);
    try {
      return await DriverAPI.sendTripChatMessage(orderId, body, sourceLanguage, attachments);
    } finally {
      setSending(false);
    }
  };

  const handleReportServiceIssue = async (issueType, note) => {
    await DriverAPI.reportTripChatIssue(orderId, issueType, note);
    await fetchChat();
  };

  const handleRecallMessage = async (messageId) => {
    if (!window.confirm(copy.confirmRecall)) return;
    await DriverAPI.recallTripChatMessage(orderId, messageId);
    await fetchChat();
  };

  const handleCreateTemplate = async (body) => {
    await DriverAPI.createChatTemplate(body);
    await fetchChat();
  };

  const handleUpdateTemplate = async (templateId, body) => {
    await DriverAPI.updateChatTemplate(templateId, body);
    await fetchChat();
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm(copy.confirmDeleteTemplate)) return;
    await DriverAPI.deleteChatTemplate(templateId);
    await fetchChat();
  };

  if (loading) {
    return <div className="driver-luxury-portal trip-chat-luxury flex min-h-screen items-center justify-center bg-black p-6 text-[#f6d982]">{copy.loading}</div>;
  }

  if (error) {
    return (
      <div className="driver-luxury-portal trip-chat-luxury min-h-screen bg-black">
        <DriverBrandHeader pageTitle={copy.pageTitle} onBack={handleBack} />
        <div className="p-4">
        <div className="rounded-2xl border border-[#d8b769]/25 bg-[#0c0a07]/90 p-4 text-[#f6d982]">
          {error}
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="driver-luxury-portal">
      <TripChatRoom
        key={chatData?.room?.id || orderId}
        title={copy.title}
        chatData={chatData}
        currentRole={currentRole}
        preferredLanguage={language}
        onPreferredLanguageChange={setLanguage}
        onSendMessage={handleSendMessage}
        onFetchMessages={fetchTripChatMessages}
        onRecallMessage={handleRecallMessage}
        onReportServiceIssue={handleReportServiceIssue}
        sending={sending}
        templates={templates}
        customTemplates={customTemplates}
        showTemplates
        onCreateTemplate={handleCreateTemplate}
        onUpdateTemplate={handleUpdateTemplate}
        onDeleteTemplate={handleDeleteTemplate}
        notificationNotice={hasNotificationNotice ? copy.newMessage : ""}
        onMessagesRead={clearMessageNotice}
        respectSafeAreaTop
        onBack={handleBack}
        backLabel={copy.back}
        driverMobileLayout
        uiCopy={copy}
      />
    </div>
  );
}
